import React, { useEffect, useState } from 'react';
import { Platform, Pressable, View } from 'react-native';
import { Image } from 'expo-image';
import { useTheme } from '@/context/ThemeContext';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import { formatFileSize, getFileTypeCategory, getFileIconByExtension } from '@/constants/utils/files';
import { getLibraryPalette } from './libraryPalette';

export const FILE_CATEGORIES = [
  { id: 'all', label: 'All files', icon: 'files' },
  { id: 'documents', label: 'Documents', icon: 'file-lines' },
  { id: 'images', label: 'Images', icon: 'image' },
  { id: 'audio', label: 'Audio', icon: 'music' },
  { id: 'video', label: 'Video', icon: 'film' },
  { id: 'code', label: 'Code', icon: 'code' },
  { id: 'other', label: 'Other', icon: 'file' },
];

function FileMenu({ file, onOpen, onDetails, onDownload, onChat, onCollect, palette }) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const actions = [
    { id: 'open', title: 'Open file', icon: 'arrow-up-right-from-square', iosIcon: 'arrow.up.right.square', onSelect: () => onOpen(file) },
    { id: 'details', title: 'Details & rename', icon: 'pencil', iosIcon: 'pencil', onSelect: () => onDetails(file) },
    { id: 'download', title: 'Download', icon: 'arrow-down-to-line', iosIcon: 'arrow.down.to.line', onSelect: () => onDownload(file) },
    { id: 'chat', title: 'Chat with file', icon: 'message-dots', iosIcon: 'bubble.left', onSelect: () => onChat(file) },
    { id: 'collection', title: 'Add to collection', icon: 'folder-plus', iosIcon: 'folder.badge.plus', onSelect: () => onCollect(file) },
  ];
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild={Platform.OS === 'web' || Platform.OS === 'macos'}>
        <Pressable accessibilityRole="button" accessibilityLabel={`Actions for ${file.name}`}
          onHoverIn={() => setHovered(true)} onHoverOut={() => setHovered(false)}
          onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
          style={{ width: 36, height: 36, borderRadius: 10 }}>
          {/* Keep the surface and focus ring inside the global menu-button reset. */}
          <View style={{ width: 36, height: 36, borderRadius: 10, alignItems: 'center', justifyContent: 'center', backgroundColor: hovered ? palette.hover : palette.surface, borderWidth: 1, borderColor: focused ? palette.activeBorder : 'transparent' }}>
            <Icon name="ellipsis" size={16} color={palette.ink} />
          </View>
        </Pressable>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="end" sideOffset={6} className={Platform.OS === 'web' ? 'DropdownMenuContent' : undefined}>
        {actions.map(action => Platform.OS === 'web'
          ? <DropdownMenuItem key={action.id} {...action} />
          : <DropdownMenu.Item key={action.id} onSelect={action.onSelect}>
            <DropdownMenu.ItemIcon ios={{ name: action.iosIcon }} />
            <DropdownMenu.ItemTitle>{action.title}</DropdownMenu.ItemTitle>
          </DropdownMenu.Item>)}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

// A file-type treatment, not a simulated preview of the file's contents.
function FileGlyph({ extension, category, palette, small }) {
  const tone = extension === 'pdf' ? palette.red
    : ['xls', 'xlsx', 'csv', 'tsv', 'ods'].includes(extension) ? palette.green
    : ['ppt', 'pptx', 'odp'].includes(extension) ? palette.orange
    : category === 'audio' || category === 'video' ? palette.purple
    : category === 'documents' ? palette.blue : palette.icon;
  if (small) return <Icon name={getFileIconByExtension(extension)} size={22} color={tone} />;
  return <View style={{ width: 72, height: 90, borderRadius: 9, backgroundColor: palette.paper, borderWidth: 1, borderColor: palette.border, alignItems: 'center', justifyContent: 'center', gap: 10,
    ...(Platform.OS === 'web' && { boxShadow: '0 3px 8px #00000008, 0 12px 24px #00000005' }) }}>
    <Icon name={getFileIconByExtension(extension)} size={29} color={tone} />
    <Text size="xxs" weight="bold" numberOfLines={1} style={{ color: tone, maxWidth: 62, letterSpacing: 0.6 }}>{extension.toUpperCase()}</Text>
  </View>;
}

export default React.memo(function LibraryFileItem({ file, width, list, compact, selected, selectionDisabled, onSelect, ...actions }) {
  const { colors, theme } = useTheme();
  const palette = getLibraryPalette(theme);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const category = getFileTypeCategory(file);
  const extension = String(file.extension || file.name?.split('.').pop() || 'file').replace(/^\./, '').toLowerCase();
  const thumbnail = category === 'images' ? file.thumbnailPath || file.cdnPath : category === 'video' ? file.thumbnailPath : null;
  useEffect(() => setImageFailed(false), [file.id, thumbnail]);
  const thumb = imageFailed ? null : thumbnail;
  const status = file.processingStatus?.status;
  const date = new Date(file.createdAt);
  const dateLabel = Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', ...(date.getFullYear() !== new Date().getFullYear() && { year: 'numeric' }) });
  const sourceLabel = file.source === 'inference' ? 'AI generated' : 'Uploaded';
  const sizeLabel = formatFileSize(file.sizeInBytes);
  const checkbox = (
    <Pressable accessibilityRole="checkbox" accessibilityLabel={`Select ${file.name}`}
      accessibilityState={{ checked: selected, disabled: selectionDisabled }} disabled={selectionDisabled}
      aria-checked={selected}
      onPress={() => onSelect(file)}
      style={({ hovered, pressed }) => ({ width: 36, height: 36, alignItems: 'center', justifyContent: 'center', borderRadius: 10, backgroundColor: hovered || pressed ? palette.hover : palette.surface })}>
      <View style={{ width: 16, height: 16, borderRadius: 5, borderWidth: 1, borderColor: selected ? palette.ink : palette.icon, backgroundColor: selected ? palette.ink : 'transparent', alignItems: 'center', justifyContent: 'center', opacity: selectionDisabled ? 0.35 : 1 }}>
        {selected && <Icon name="check" size={10} color={palette.onInk} />}
      </View>
    </Pressable>
  );
  const preview = (
    <View style={{ height: list ? 42 : compact ? 142 : 172, width: list ? 42 : '100%', flexShrink: 0, borderRadius: list ? 9 : 12, backgroundColor: palette.preview, alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
      {thumb ? <Image source={{ uri: thumb }} contentFit={list ? 'cover' : 'contain'} style={{ width: '100%', height: '100%' }} onError={() => setImageFailed(true)} accessibilityLabel={file.name} />
        : <FileGlyph extension={extension} category={category} palette={palette} small={list} />}
      {!list && category === 'video' && thumb && <View pointerEvents="none" style={{ position: 'absolute', width: 36, height: 36, borderRadius: 18, backgroundColor: '#00000080', alignItems: 'center', justifyContent: 'center' }}><Icon name="play" size={12} color="#FFFFFF" /></View>}
    </View>
  );
  const processing = status && !['completed', 'skipped'].includes(status)
    ? <Text size="xxs" style={{ color: status === 'failed' ? colors.color_destructive : colors.text_color_secondary }}>{status === 'failed' ? 'Processing failed' : 'Preparing for chat…'}</Text> : null;
  const interactions = {
    accessibilityRole: 'button', accessibilityLabel: `Open ${file.name}`,
    accessibilityHint: `${extension.toUpperCase()}, ${sizeLabel}, ${sourceLabel}, ${dateLabel}`,
    onPress: () => actions.onOpen(file),
    onHoverIn: () => setHovered(true), onHoverOut: () => setHovered(false),
    onFocus: () => setFocused(true), onBlur: () => setFocused(false),
  };

  if (list) return (
    <View style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 12, paddingHorizontal: compact ? 4 : 12, gap: compact ? 6 : 12, borderBottomWidth: 1, borderColor: palette.border, backgroundColor: selected ? palette.selected : hovered || focused ? palette.hover : 'transparent' }}>
      {checkbox}
      <Pressable {...interactions} style={{ flex: 1, minWidth: 0, flexDirection: 'row', alignItems: 'center', gap: 12 }}>
        {preview}
        <View style={{ flex: 1, minWidth: 0, gap: 4 }}>
          <Text size="s" weight="medium" numberOfLines={1}>{file.name}</Text>
          <Text size="xs" color="secondary" numberOfLines={1}>{compact ? `${extension.toUpperCase()} · ${sizeLabel}` : sourceLabel}</Text>
          {processing}
        </View>
        {!compact && <>
          <Text size="xs" color="secondary" numberOfLines={1} style={{ width: 70 }}>{extension.toUpperCase()}</Text>
          <Text size="xs" color="secondary" style={{ width: 98 }}>{dateLabel}</Text>
          <Text size="xs" color="secondary" style={{ width: 72, textAlign: 'right' }}>{sizeLabel}</Text>
        </>}
      </Pressable>
      <FileMenu file={file} palette={palette} {...actions} />
    </View>
  );

  return (
    <View style={{ width, padding: 5, borderRadius: 17, borderWidth: 1, borderColor: selected ? palette.activeBorder : hovered || focused ? palette.border : 'transparent', backgroundColor: selected ? palette.selected : hovered || focused ? palette.surface : 'transparent' }}>
      <Pressable {...interactions} style={({ pressed }) => ({ borderRadius: 12, opacity: pressed ? 0.75 : 1 })}>
        {preview}
        <View style={{ paddingHorizontal: 5, paddingTop: 12, paddingBottom: 6, gap: 5 }}>
          <Text size="s" weight="medium" numberOfLines={2} style={{ minHeight: 38, lineHeight: 19, ...(Platform.OS === 'web' && { overflowWrap: 'anywhere' }) }}>{file.name}</Text>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 4 }}>
            <Text size="xxs" color="secondary" numberOfLines={1} style={{ flexShrink: 1 }}>{extension.toUpperCase()} · {sizeLabel}</Text>
            <Text size="xxs" color="secondary">{dateLabel}</Text>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
            <Icon name={file.source === 'inference' ? 'sparkles' : 'arrow-up-to-line'} size={10} color={palette.icon} />
            <Text size="xxs" color="secondary">{sourceLabel}</Text>
          </View>
          {processing}
        </View>
      </Pressable>
      <View style={{ position: 'absolute', top: 12, left: 12 }}>{checkbox}</View>
      <View style={{ position: 'absolute', top: 12, right: 12 }}><FileMenu file={file} palette={palette} {...actions} /></View>
    </View>
  );
});
